import { type CSSProperties, memo, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { BodyCopy } from "../components/BodyCopy";
import {
  Button,
  HomeAddressIcon,
  Layout,
  SupportIcon,
  Title,
} from "lola-framework-ui-test";
import { AuraLayout } from "../components/AuraLayout";
import { FloatingField } from "../components/FloatingField";
import { useStore } from "../store/globalState";
import { useSessionStore } from "../store/useSessionStore";
import { useClaimLink } from "../hooks/useClaimLink";
import { useRouteWithToken } from "../hooks/useRouteWithToken";
import { useWatchedOperation } from "../hooks/useWatchedOperation";
import { APP_ROUTES, TITLE_COLOR } from "../constants";
import { RequireActiveClerkSession } from "../hooks/useRequireActiveClerkSession";
import { COUNTRIES } from "../constants/countries";
import {
  submitRecipientScreening,
  isDeadLinkError,
  type IRecipientScreeningRequest,
} from "../services/api";
import { hasOfacDisambiguation } from "../utils/operationStatus";
import { parseBindError } from "../utils/bindErrors";
import {
  getMockOfacOutcome,
  isMockOfacEnabled,
  setMockOfacOutcome,
  type MockOfacOutcome,
} from "../mocks/ofacDisambiguation";
import { getListStub, setListStub, type ListStub } from "../utils/listStub";
import { getEvents, safeCapture } from "../analytics";
import { useSupportSheet } from "../hooks/useSupportSheet";
import { ScrollFadeIndicator } from "../components/ScrollFadeIndicator";
import { Navbar } from "../components/Navbar";
import { loadZipLookup, zipLookupKey, type ZipLookup } from "../utils/zip-lookup";


interface FormState {
  dateOfBirth: string;
  line1: string;
  line2: string;
  city: string;
  state: string;
  zip: string;
  countryISO2: string;
}

interface FormErrors {
  dateOfBirth?: string;
  line1?: string;
  city?: string;
  state?: string;
  zip?: string;
  countryISO2?: string;
}

/**
 * Post-op soft OFAC gate: collect DOB / address to disambiguate a list match.
 */
export const OfacDisambiguationPage = memo(() => {
  const { operationId } = useParams<{ operationId: string }>();
  return (
    <RequireActiveClerkSession
      resume={
        operationId
          ? { kind: "ofac", operationId }
          : { kind: "transactions" }
      }
    >
      <OfacDisambiguationPageContent />
    </RequireActiveClerkSession>
  );
});

const OfacDisambiguationPageContent = memo(() => {
  useClaimLink();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { theme } = useStore();
  const { session } = useSessionStore();
  const { redirectTo } = useRouteWithToken();
  const { operationId } = useParams<{ operationId: string }>();
  const { openSupport, supportSheet } = useSupportSheet();

  const colorConfig = theme.whiteView;
  const { bodyCopy, dropzoneColors, backgroundBtn, textColorBtn } =
    colorConfig;
  const fieldColors = {
    background: colorConfig.background,
    title: colorConfig.title,
    bodyCopy,
    inactiveColor: colorConfig.subtitile,
    errorColor: colorConfig.errorColor,
  };

  const linkId = session.data?.linkId;
  const destinationCountry =
    session.data?.config?.sendCommitment?.destinationCountry ??
    session.data?.config?.destinationCountry ??
    "MX";

  const { operation, status: watchStatus } = useWatchedOperation(
    linkId,
    operationId
  );

  const requiredFields = useMemo(
    () => operation?.actionRequired?.requiredFields ?? ["dateOfBirth", "address"],
    [operation?.actionRequired?.requiredFields]
  );
  const needDob = requiredFields.includes("dateOfBirth");
  const needAddress = requiredFields.includes("address");

  const [form, setForm] = useState<FormState>({
    dateOfBirth: "",
    line1: "",
    line2: "",
    city: "",
    state: "",
    zip: "",
    countryISO2: destinationCountry,
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitting, setSubmitting] = useState(false);
  const [stillRequired, setStillRequired] = useState(false);
  const [screeningDown, setScreeningDown] = useState(false);
  const [mockEnabled, setMockEnabled] = useState(false);
  const [listStub, setListStubState] = useState<ListStub | null>(() => getListStub());
  const [mockOutcome, setMockOutcomeState] = useState<MockOfacOutcome>("clear");
  const [zipLookup, setZipLookup] = useState<ZipLookup | null>(null);

  const isUS = form.countryISO2 === "US";

  useEffect(() => {
    if (!isUS) return;
    let active = true;
    loadZipLookup()
      .then((lookup) => {
        if (active) setZipLookup(() => lookup);
      })
      .catch(() => {
        // Keep the submit disabled until city and state can be resolved.
      });
    return () => {
      active = false;
    };
  }, [isUS]);

  const zipLocation = useMemo(() => {
    if (!isUS || !zipLookup) return null;
    const key = zipLookupKey(form.zip);
    return key ? zipLookup(key) : null;
  }, [form.zip, isUS, zipLookup]);
  const zipUnknown =
    isUS && Boolean(zipLookup) && Boolean(zipLookupKey(form.zip)) && !zipLocation;
  const usAddressReady = Boolean(form.line1.trim()) && Boolean(zipLocation);

  useEffect(() => {
    setMockEnabled(isMockOfacEnabled());
    setMockOutcomeState(getMockOfacOutcome());
  }, []);

  useEffect(() => {
    safeCapture(getEvents().OFAC_VIEW);
  }, []);

  useEffect(() => {
    if (watchStatus !== "ready" || !operation) return;
    if (!hasOfacDisambiguation(operation)) {
      if (operation.status === "in_review") {
        navigate(APP_ROUTES.CLAIM_BLOCKED_PAGE, {
          replace: true,
          state: { reason: "recipient_review" },
        });
        return;
      }
      redirectTo(APP_ROUTES.TRANSACTIONS_PAGE, { replace: true });
    }
  }, [watchStatus, operation, redirectTo, navigate]);

  const handleChange = (field: keyof FormState, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field as keyof FormErrors]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
    setStillRequired(false);
    setScreeningDown(false);
  };

  const validate = (): FormErrors => {
    const errs: FormErrors = {};
    if (needDob && !form.dateOfBirth) {
      errs.dateOfBirth = t("ofacDisambiguation.dateOfBirthRequired");
    }
    if (needAddress) {
      if (!form.line1.trim()) errs.line1 = t("ofacDisambiguation.streetRequired");
      if (isUS) {
        if (!/^\d{5}(-\d{4})?$/.test(form.zip.trim())) {
          errs.zip = t("ofacDisambiguation.zipRequired");
        } else if (!zipLocation) {
          errs.zip = t("ofacDisambiguation.zipNotFound");
        }
      } else {
        if (!form.city.trim()) errs.city = t("ofacDisambiguation.cityRequired");
        if (!form.state.trim()) errs.state = t("ofacDisambiguation.stateRequired");
        if (!form.zip.trim()) errs.zip = t("ofacDisambiguation.zipRequired");
      }
      if (!form.countryISO2) {
        errs.countryISO2 = t("ofacDisambiguation.countryRequired");
      }
    }
    return errs;
  };

  const handleSubmit = async (event?: React.FormEvent) => {
    event?.preventDefault();
    if (!linkId || !operationId) return;

    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    const body: IRecipientScreeningRequest = {};
    if (needDob) body.dateOfBirth = form.dateOfBirth;
    if (needAddress) {
      body.address = {
        line1: form.line1.trim(),
        line2: form.line2.trim() || undefined,
        city: (isUS ? zipLocation?.city : form.city.trim()) || "",
        state: (isUS ? zipLocation?.stateCode : form.state.trim()) || undefined,
        zip: form.zip.trim() || undefined,
        countryISO2: form.countryISO2,
      };
    }

    try {
      setSubmitting(true);
      const updated = await submitRecipientScreening(linkId, operationId, body);
      if (hasOfacDisambiguation(updated)) {
        safeCapture(getEvents().OFAC_SUBMIT, {
          success: false,
          error_code: "STILL_REQUIRED",
        });
        setStillRequired(true);
        return;
      }
      safeCapture(getEvents().OFAC_SUBMIT, { success: true });
      if (updated.status === "in_review") {
        navigate(APP_ROUTES.CLAIM_BLOCKED_PAGE, {
          replace: true,
          state: { reason: "recipient_review" },
        });
        return;
      }
      redirectTo(APP_ROUTES.TRANSACTIONS_PAGE, { replace: true });
    } catch (err) {
      // Link expired / revoked: the dead-link handler already routed to the
      // screen that explains why the link stopped working.
      if (isDeadLinkError(err)) {
        return;
      }

      const { reason, code } = parseBindError(err);
      if (code === "RECIPIENT_SCREENING_UNAVAILABLE") {
        safeCapture(getEvents().OFAC_SUBMIT, {
          success: false,
          error_code: code,
        });
        setScreeningDown(true);
        return;
      }
      if (reason === "ofac") {
        safeCapture(getEvents().OFAC_SUBMIT, {
          success: false,
          error_code: code ?? "OFAC_HIT",
        });
        navigate(APP_ROUTES.CLAIM_BLOCKED_PAGE, {
          replace: true,
          state: { reason: "ofac" },
        });
        return;
      }
      safeCapture(getEvents().OFAC_SUBMIT, {
        success: false,
        error_code: code ?? "OFAC_SUBMIT_FAILED",
      });
      navigate(APP_ROUTES.ERROR_PAGE, {
        state: {
          title: t("error.title"),
          subTitle: null,
          message:
            err instanceof Error ? err.message : t("error.tryAgainLater"),
          btnText: t("common.retry"),
          isOut: false,
          pageUrl: `${APP_ROUTES.OFAC_DISAMBIGUATION_PAGE}/${operationId}`,
        },
      });
    } finally {
      setSubmitting(false);
    }
  };

  const errorStyle: React.CSSProperties = {
    color: "#dc251c",
    fontSize: "0.75rem",
    marginTop: 4,
  };

  if (watchStatus === "loading") {
    return (
      <AuraLayout colorConfig={colorConfig}>
        <Layout.Content>
          <section className="receipt-summary-loading">
            <span
              className="loader-spinner"
              style={
                {
                  "--spinner-start": bodyCopy,
                  "--spinner-end": bodyCopy,
                } as CSSProperties
              }
            />
          </section>
        </Layout.Content>
      </AuraLayout>
    );
  }

  if (!operation || !hasOfacDisambiguation(operation)) {
    return null;
  }

  return (
    <>
      <AuraLayout colorConfig={colorConfig} className="scrollable need-help-header">
        <Layout.Header>
          <Navbar
            title={t("common.needHelp")}
            align="right"
            onBackClick={() => redirectTo(APP_ROUTES.TRANSACTIONS_PAGE)}
            onIconClick={() => openSupport()}
            icon={<SupportIcon size={35} colors={dropzoneColors} />}
          />
        </Layout.Header>
        <Layout.Content className="transfer-section">
          <ScrollFadeIndicator color={bodyCopy} />
          <Title
            title={t("ofacDisambiguation.title")}
            align="left"
            color={TITLE_COLOR}
          />
          <BodyCopy textColor={bodyCopy} className="ofac-disambiguation-helper">
            {t("ofacDisambiguation.helper")}
          </BodyCopy>

          {listStub && (
            <section className="ofac-mock-controls" aria-label={t("ofacDisambiguation.stubLabel")}>
              <BodyCopy textColor={bodyCopy}>
                {t("ofacDisambiguation.stubLabel")}: <b>{listStub}</b>
              </BodyCopy>
              <div className="ofac-mock-buttons">
                {(
                  [
                    ["form", "stubClear"],
                    ["block", "stubReview"],
                    ["down", "stubDown"],
                  ] as const
                ).map(([value, label]) => (
                  <button
                    key={value}
                    type="button"
                    className={`ofac-mock-btn ${listStub === value ? "active" : ""}`}
                    onClick={() => {
                      setListStub(value);
                      setListStubState(value);
                    }}
                  >
                    {t(`ofacDisambiguation.${label}`)}
                  </button>
                ))}
              </div>
            </section>
          )}

          {mockEnabled && !listStub && (
            <section className="ofac-mock-controls" aria-label="OFAC mock">
              <BodyCopy textColor={bodyCopy}>
                {t("ofacDisambiguation.mockOutcome")}: <b>{mockOutcome}</b>
              </BodyCopy>
              <div className="ofac-mock-buttons">
                {(["clear", "still_required", "hit"] as MockOfacOutcome[]).map(
                  (outcome) => (
                    <button
                      key={outcome}
                      type="button"
                      className={`ofac-mock-btn ${mockOutcome === outcome ? "active" : ""}`}
                      onClick={() => {
                        setMockOfacOutcome(outcome);
                        setMockOutcomeState(outcome);
                      }}
                    >
                      {outcome === "clear"
                        ? t("ofacDisambiguation.mockClear")
                        : outcome === "still_required"
                          ? t("ofacDisambiguation.mockStill")
                          : t("ofacDisambiguation.mockHit")}
                    </button>
                  )
                )}
              </div>
            </section>
          )}

          <form
            id="ofac-disambiguation-form"
            className="mock-card-form"
            autoComplete="on"
            onSubmit={handleSubmit}
          >
            <section className="mock-card-fields">
              {needDob && (
                <div className="mock-card-field">
                  <FloatingField
                    label={t("ofacDisambiguation.dateOfBirth")}
                    value={form.dateOfBirth || " "}
                    required
                    isValid={!errors.dateOfBirth}
                    colors={fieldColors}
                  >
                    <input
                      id="ofac-dob"
                      style={{ color: bodyCopy }}
                      type="date"
                      name="dateOfBirth"
                      autoComplete="bday"
                      value={form.dateOfBirth}
                      onChange={(e) => handleChange("dateOfBirth", e.target.value)}
                    />
                  </FloatingField>
                  {errors.dateOfBirth && (
                    <div style={errorStyle}>{errors.dateOfBirth}</div>
                  )}
                </div>
              )}

              {needAddress && (
                <>
                  <div className="mock-card-field">
                    <FloatingField
                      label={t("ofacDisambiguation.street")}
                      value={form.line1}
                      required
                      isValid={!errors.line1}
                      colors={fieldColors}
                      icon={<HomeAddressIcon colors={dropzoneColors} size={18} />}
                    >
                      <input
                        style={{ color: bodyCopy }}
                        name="line1"
                        autoComplete="address-line1"
                        value={form.line1}
                        onChange={(e) => handleChange("line1", e.target.value)}
                      />
                    </FloatingField>
                    {errors.line1 && <div style={errorStyle}>{errors.line1}</div>}
                  </div>
                  <div className="mock-card-field">
                    <FloatingField
                      label={t("ofacDisambiguation.street2")}
                      value={form.line2}
                      colors={fieldColors}
                    >
                      <input
                        style={{ color: bodyCopy }}
                        name="line2"
                        autoComplete="address-line2"
                        value={form.line2}
                        onChange={(e) => handleChange("line2", e.target.value)}
                      />
                    </FloatingField>
                  </div>
                  {!isUS && (
                    <>
                      <div className="mock-card-field">
                        <FloatingField
                          label={t("ofacDisambiguation.city")}
                          value={form.city}
                          required
                          isValid={!errors.city}
                          colors={fieldColors}
                        >
                          <input
                            style={{ color: bodyCopy }}
                            name="city"
                            autoComplete="address-level2"
                            value={form.city}
                            onChange={(e) => handleChange("city", e.target.value)}
                          />
                        </FloatingField>
                        {errors.city && <div style={errorStyle}>{errors.city}</div>}
                      </div>
                      <div className="mock-card-field">
                        <FloatingField
                          label={t("ofacDisambiguation.state")}
                          value={form.state}
                          required
                          isValid={!errors.state}
                          colors={fieldColors}
                        >
                          <input
                            style={{ color: bodyCopy }}
                            name="state"
                            autoComplete="address-level1"
                            value={form.state}
                            onChange={(e) => handleChange("state", e.target.value)}
                          />
                        </FloatingField>
                        {errors.state && <div style={errorStyle}>{errors.state}</div>}
                      </div>
                    </>
                  )}
                  <div className="mock-card-field">
                    <FloatingField
                      label={t("ofacDisambiguation.zip")}
                      value={form.zip}
                      required
                      isValid={!errors.zip}
                      colors={fieldColors}
                    >
                      <input
                        style={{ color: bodyCopy }}
                        name="zip"
                        autoComplete="postal-code"
                        inputMode={isUS ? "numeric" : "text"}
                        value={form.zip}
                        onChange={(e) =>
                          handleChange(
                            "zip",
                            isUS
                              ? e.target.value.replace(/[^\d-]/g, "")
                              : e.target.value
                          )
                        }
                      />
                    </FloatingField>
                    {(errors.zip || zipUnknown) && (
                      <div style={errorStyle}>
                        {errors.zip ?? t("ofacDisambiguation.zipNotFound")}
                      </div>
                    )}
                  </div>
                  <div className="mock-card-field">
                    <FloatingField
                      label={t("ofacDisambiguation.country")}
                      value={form.countryISO2}
                      required
                      isValid={!errors.countryISO2}
                      colors={fieldColors}
                    >
                      <select
                        style={{ color: bodyCopy }}
                        name="country"
                        value={form.countryISO2}
                        onChange={(e) => {
                          handleChange("countryISO2", e.target.value);
                          handleChange("state", "");
                        }}
                      >
                        <option value="" />
                        {COUNTRIES.map((c) => (
                          <option key={c.code} value={c.code}>
                            {c.name}
                          </option>
                        ))}
                      </select>
                    </FloatingField>
                    {errors.countryISO2 && (
                      <div style={errorStyle}>{errors.countryISO2}</div>
                    )}
                  </div>
                </>
              )}
            </section>

            {screeningDown && (
              <BodyCopy textColor={bodyCopy} className="ofac-disambiguation-helper">
                {t("ofacDisambiguation.unavailable")}
              </BodyCopy>
            )}
            {stillRequired && (
              <BodyCopy textColor="#d97706" className="ofac-still-required">
                {t("ofacDisambiguation.stillRequired")}
              </BodyCopy>
            )}
          </form>
        </Layout.Content>
        <Layout.Footer>
          <section className="footer-section">
            <Button
              size="large"
              background={backgroundBtn}
              color={textColorBtn}
              disabled={submitting || (needAddress && isUS && !usAddressReady)}
              onClick={handleSubmit}
            >
              {submitting
                ? t("ofacDisambiguation.submitting")
                : t("ofacDisambiguation.submit")}
            </Button>
          </section>
        </Layout.Footer>
      </AuraLayout>
      {supportSheet}
    </>
  );
});
